import React from "react";
import Link from "next/link";
import Image from "next/image";
import * as ICONS from "@/icons";
import * as ROUTES from "@/constants/routes";
import { toCapitalize } from "@/utils";
import { CommunityOverview } from "@/models/community.model";
import { CommunityTab, handleCommunityTextColors } from "./utils";

interface Props {
  community: CommunityOverview;
}

const CommunityHighlight = ({ community }: Props) => {
  function checkEmptyProperties(obj: any) {
    for (var key in obj) {
      if (obj[key] !== null && obj[key] != "") return false;
    }
    return true;
  }

  return (
    <div className="flex h-fit flex-col justify-between bg-card p-6 lg:sticky lg:top-24 lg:w-1/3">
      <div>
        <div className="flex flex-col-reverse justify-between sm:flex-row sm:items-center">
          <div className="mr-4">
            <h2 className="mt-2 text-2xl font-medium text-gray-100 sm:mt-0">
              {community.name}
            </h2>
            <p className="text-sm">
              {community.type && (
                <span
                  className={handleCommunityTextColors(
                    community.type as CommunityTab
                  )}
                >
                  {toCapitalize(community.type)}
                </span>
              )}
              {community.foundingYear && (
                <span className="mx-2">
                  &bull; Since {community.foundingYear}
                </span>
              )}
            </p>
          </div>
          {community.logo ? (
            <div className="relative aspect-square h-16 w-16">
              <Image src={community.thumbnail} layout="fill" alt="" />
            </div>
          ) : null}
        </div>

        <p className="mt-4 mb-12">{community.description}</p>
      </div>

      <Link href={`${ROUTES.COMMUNITY}/${community.tag}`}>
        <a className="mt-4 inline-block w-fit text-sm text-blue-400 opacity-100 hover:opacity-100">
          Read more
        </a>
      </Link>
      {checkEmptyProperties(community.socialMedia) ? null : (
        <hr className="mt-4 border-slate-700" />
      )}
      <ul className="flex flex-shrink space-x-4">
        {community.socialMedia?.discord && (
          <SocialMedia link={community.socialMedia?.discord}>
            <ICONS.DiscordIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.twitch && (
          <SocialMedia link={community.socialMedia?.twitch}>
            <ICONS.TwitchIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.twitter && (
          <SocialMedia link={community.socialMedia?.twitter}>
            <ICONS.TwitterIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.facebook && (
          <SocialMedia link={community.socialMedia?.facebook}>
            <ICONS.FacebookIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.instagram && (
          <SocialMedia link={community.socialMedia?.instagram}>
            <ICONS.InstagramIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.youtube && (
          <SocialMedia link={community.socialMedia?.youtube}>
            <ICONS.YoutubeIcon />
          </SocialMedia>
        )}
        {community.socialMedia?.website && (
          <SocialMedia link={community.socialMedia?.website}>
            <ICONS.UrlIcon />
          </SocialMedia>
        )}
      </ul>
    </div>
  );
};

const SocialMedia = (props: { link: string; children: React.ReactNode }) => {
  return (
    <li className="mt-4 text-xl hover:text-gray-200">
      <Link href={props.link}>
        <a target="_blank" rel="noopener noreferrer">
          {props.children}
        </a>
      </Link>
    </li>
  );
};

export default CommunityHighlight;
